import {
	type HTMLAttributes,
	type InputHTMLAttributes,
	type KeyboardEvent,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react';

import { getDocument } from '@atlaskit/browser-apis';
import { type Agent } from '@atlassian/conversation-assistant-service-api/types/assistance-service';

interface UseAgentMenuComboBoxOptions {
	items: Agent[];
	isOpen: boolean;
	onSelect: (agent: Agent) => void;
	onClose: () => void;
	enabled?: boolean;
}

/** scrolls just past item to trigger infinite scroll */
function scrollOptionIntoView(optionId: string) {
	const el = getDocument()?.getElementById(optionId);
	const scrollParent = el?.closest('[role="listbox"]');
	if (!el || !scrollParent) {
		return;
	}

	const elRect = el.getBoundingClientRect();
	const parentRect = scrollParent.getBoundingClientRect();
	const padding = 4;

	if (elRect.bottom + padding > parentRect.bottom) {
		scrollParent.scrollTop += elRect.bottom - parentRect.bottom + padding;
	} else if (elRect.top - padding < parentRect.top) {
		scrollParent.scrollTop -= parentRect.top - elRect.top + padding;
	}
}

const emptyProps: HTMLAttributes<HTMLElement> = {};
const emptyOptionProps = () => emptyProps;

export function useAgentMenuComboBox({
	items,
	isOpen,
	onSelect,
	onClose,
	enabled,
}: UseAgentMenuComboBoxOptions): {
	inputProps: InputHTMLAttributes<HTMLInputElement>;
	listBoxProps: HTMLAttributes<HTMLElement>;
	getOptionProps: (index: number) => HTMLAttributes<HTMLElement>;
	focusedIndex: number;
	setFocused: (index: number, { scroll }?: { scroll?: boolean }) => void;
} {
	const listBoxId = `agent-menu-listbox`;
	const getOptionId = (index: number) => `agent-menu-option-${index}`;

	const [focusedIndex, setFocusedIndex] = useState(-1);

	const itemsRef = useRef(items);
	itemsRef.current = items;

	const setFocused = useCallback((index: number, { scroll = true }: { scroll?: boolean } = {}) => {
		setFocusedIndex(index);
		if (scroll && index >= 0) {
			requestAnimationFrame(() => scrollOptionIntoView(getOptionId(index)));
		}
	}, []);

	useEffect(() => {
		if (!enabled) {
			return;
		}

		if (!isOpen) {
			setFocused(-1);
		}
	}, [isOpen, enabled, setFocused]);

	const onKeyDown = useCallback(
		(e: KeyboardEvent) => {
			if (!isOpen) {
				return;
			}

			const count = itemsRef.current.length;

			switch (e.key) {
				case 'ArrowDown':
					e.preventDefault();
					setFocused(Math.min(focusedIndex + 1, count - 1));
					break;
				case 'ArrowUp':
					e.preventDefault();
					setFocused(Math.max(focusedIndex - 1, 0));
					break;
				case 'Enter':
					if (focusedIndex >= 0 && focusedIndex < count) {
						e.preventDefault();
						onSelect(itemsRef.current[focusedIndex]);
					}
					break;
				case 'Escape':
					e.preventDefault();
					e.stopPropagation();
					onClose();
					break;
			}
		},
		[isOpen, focusedIndex, onSelect, onClose, setFocused],
	);

	if (!enabled) {
		return {
			inputProps: {} as InputHTMLAttributes<HTMLInputElement>,
			listBoxProps: {} as HTMLAttributes<HTMLElement>,
			getOptionProps: emptyOptionProps,
			focusedIndex: -1,
			setFocused: () => {},
		};
	}

	const activedescendant = focusedIndex >= 0 ? getOptionId(focusedIndex) : undefined;

	const inputProps: InputHTMLAttributes<HTMLInputElement> = {
		role: 'combobox',
		'aria-expanded': isOpen,
		'aria-controls': listBoxId,
		'aria-activedescendant': activedescendant,
		'aria-autocomplete': 'list',
		'aria-haspopup': 'listbox',
		onKeyDown,
	};

	const listBoxProps: HTMLAttributes<HTMLElement> = {
		role: 'listbox',
		id: listBoxId,
		'aria-label': 'Agents',
	};

	const getOptionProps = (index: number): HTMLAttributes<HTMLElement> => ({
		role: 'option',
		id: getOptionId(index),
		'aria-selected': focusedIndex === index,
		onMouseMove: () => setFocused(index, { scroll: false }),
	});

	return {
		inputProps,
		listBoxProps,
		getOptionProps,
		focusedIndex,
		setFocused,
	};
}
